import React, { useState } from "react";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { useAuth } from "../lib/auth";
import { coaches } from "../data/appdata";
import { COUNTRIES } from "../data/countries";

export default function Signup({ onNavigate }) {
  const { signup } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", displayName: "", gender: "",
    city: "", country: "", preferredUnits: "metric", preferredCoach: "amara",
  });
  const [agreed, setAgreed] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const goToCoachStep = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error("Please fill in name, email and password");
      return;
    }
    setStep(2);
  };

  const finishSignup = async () => {
    if (!agreed) { toast.error("Please agree to the Terms & Privacy Policy to continue"); return; }
    setBusy(true);
    try {
      const ref = sessionStorage.getItem("wyvn_ref") || undefined;
      await signup({ ...form, acceptedTerms: true, ...(ref ? { ref } : {}) });
      sessionStorage.removeItem("wyvn_ref");
      onNavigate("dashboard");
    } catch (err) {
      toast.error("Sign up failed", { description: err.message });
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-5 py-12">
      <div className="pointer-events-none absolute -top-32 right-1/4 h-[500px] w-[600px] rounded-full bg-brand-deep/15 blur-[150px]" />
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-30" />

      <div className="relative w-full max-w-md">
        <button
          onClick={() => (step === 2 ? setStep(1) : onNavigate("landing"))}
          data-testid="signup-back-btn"
          className="mb-6 inline-flex items-center gap-2 text-sm text-mist-muted transition-colors hover:text-mist"
        >
          <ArrowLeft size={15} /> {step === 2 ? "Back" : "Back"}
        </button>

        <div className="glass rounded-[2rem] p-8">
          <Logo size={30} />

          {step === 1 && (
            <>
              <h1 className="mt-7 text-3xl font-semibold text-mist">Start free</h1>
              <p className="mt-2 text-sm text-mist-muted">30-day trial · no card needed.</p>

              <form onSubmit={goToCoachStep} className="mt-7 space-y-4">
                <Field label="Full name">
                  <input required value={form.name} onChange={set("name")} data-testid="signup-name" placeholder="Alex Rivera" className="aeva-input" />
                </Field>
                <Field label="Email">
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="signup-email" placeholder="you@email.com" className="aeva-input" />
                </Field>
                <Field label="Password">
                  <input required type="password" value={form.password} onChange={set("password")} data-testid="signup-password" placeholder="Create a password" className="aeva-input" />
                </Field>
                <Field label="Display name (optional · shown on posts for privacy)">
                  <input value={form.displayName} onChange={set("displayName")} data-testid="signup-displayname" placeholder="e.g. Where in the world is mayor?" className="aeva-input" />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="City">
                    <input value={form.city} onChange={set("city")} data-testid="signup-city" placeholder="Lisbon" className="aeva-input" />
                  </Field>
                  <Field label="Country">
                    <select value={form.country} onChange={set("country")} data-testid="signup-country" className="aeva-input">
                      <option value="">Select country</option>
                      {COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}
                    </select>
                  </Field>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-mist-muted">Gender (optional · personalizes cycle & wellness)</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[["female", "Female"], ["male", "Male"], ["", "Prefer not to say"]].map(([g, label]) => (
                      <button
                        type="button" key={label} onClick={() => setForm((f) => ({ ...f, gender: g }))}
                        data-testid={`signup-gender-${g || "none"}`}
                        className={`flex items-center justify-center gap-1.5 rounded-xl border px-2 py-2.5 text-xs transition-all ${
                          form.gender === g ? "border-brand bg-brand/10 text-mist" : "border-white/10 text-mist-muted hover:border-white/20"
                        }`}
                      >
                        {form.gender === g && <Check size={13} className="text-brand" />} {label}
                      </button>
                    ))}
                  </div>
                </div>

                <button type="submit" data-testid="signup-continue-btn" className="btn-primary flex w-full items-center justify-center gap-2">
                  Continue <ArrowRight size={16} />
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-mist-muted">
                Already a member?{" "}
                <button onClick={() => onNavigate("login")} className="font-medium text-brand hover:underline">Sign in</button>
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="mt-7 text-2xl font-semibold text-mist">Pick your coach</h1>
              <p className="mt-2 text-sm text-mist-muted">Who do you want in your corner? You can switch anytime.</p>

              <div className="mt-6 space-y-3" data-testid="signup-coach-list">
                {coaches.map((c) => {
                  const selected = form.preferredCoach === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, preferredCoach: c.id }))}
                      data-testid={`signup-coach-${c.id}`}
                      className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-all ${
                        selected ? "border-brand bg-brand/10" : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-display text-lg font-bold" style={{ background: `${c.accent}22`, color: c.accent }}>{c.name[0]}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-mist">{c.name}</span>
                          <span className="text-[11px] uppercase tracking-wider" style={{ color: c.accent }}>{c.role}</span>
                        </div>
                        <p className="mt-0.5 truncate text-xs text-mist-muted">{c.tagline}</p>
                      </div>
                      {selected && <Check size={18} className="shrink-0 text-brand" />}
                    </button>
                  );
                })}
              </div>

              <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3.5" data-testid="signup-terms-row">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="signup-terms-checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-brand" />
                <span className="text-xs leading-relaxed text-mist-muted">
                  I agree to WYVN's{" "}
                  <a href="/#terms" target="_blank" rel="noreferrer" className="text-brand hover:underline" data-testid="signup-terms-link">Terms of Use (EULA)</a>{" "}
                  and{" "}
                  <a href="/#privacy" target="_blank" rel="noreferrer" className="text-brand hover:underline" data-testid="signup-privacy-link">Privacy Policy</a>. I understand there is zero tolerance for objectionable content or abusive behavior.
                </span>
              </label>

              <button onClick={finishSignup} disabled={busy || !agreed} data-testid="signup-finish-btn" className="btn-primary mt-4 flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50">
                {busy ? "Creating your account…" : <>Create account <ArrowRight size={16} /></>}
              </button>
              <p className="mt-3 text-center text-xs text-mist-muted">You'll start with a 30-day Premium Elite trial — all five coaches unlocked.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-mist-muted">{label}</label>
      {children}
    </div>
  );
}
